/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { AnimatePresence, motion } from "framer-motion";
import { FC, useEffect, useMemo, useRef, useState } from "react";

import { touchVariants } from "~/lib/motion-obj";
import { cn } from "~/lib/utils";
import BannerView from "./BannerItem";

import { useTranslation } from "react-i18next";
import { IoIosArrowRoundForward } from "react-icons/io";
import useAuthRoute from "~/hook/useAuthRoute";
import { useFontStyle } from "~/hook/useFontStyle";
import useImagePreloader from "~/hook/useImagePreloader";
import { getSession, removeSession, setSession } from "~/lib/helperFuncation/sessionStorageService";
import { AUTH_ROUTE, CATEGORY, FALLBACK_IMAGE_URL, SCROLL_STORAGE_KEY, SELECTED_ITEM } from "~/utils/constant";
import { wantsMobile } from "~/utils/helper-funcations";
import ImageWithSkeleton from "../ImageWithSkeleton";
import MobileIconItem from "./IconItem";
import ListView from "./ListView";
import TextItemView from "./TextItem";
import { MenuDataType, PropsType } from "./type";

const COMPONENT_MAPPING: Record<string, React.FC<PropsType>> = {
  icon: MobileIconItem,
  banner: BannerView,
  list: ListView,
  text: TextItemView,
};

const NAVIGATION = "navigation";

// Slide animation variants for right-panel content when category changes
const slideVariants = {
  enter: (direction: "next" | "prev") => ({
    opacity: 0,
    y: direction === "next" ? 28 : -28,
  }),
  center: { opacity: 1, y: 0 },
  exit: (direction: "next" | "prev") => ({
    opacity: 0,
    y: direction === "next" ? -28 : 28,
  }),
};

type MobileMenuProps = {
  categories: MenuDataType[];
  onClose?: () => void;
};

const MobileMenu: FC<MobileMenuProps> = ({ categories, onClose }) => {
  const { medium } = useFontStyle();
  const { t } = useTranslation();

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { isImagePreloaded } = useImagePreloader(categories);
  const { handleNavigate } = useAuthRoute();

  const [categoryId, setCategoryId] = useState<number>(Number(getSession(CATEGORY)) || categories[0].id);
  const [prevCategoryId, setPrevCategoryId] = useState<number>(1);

  // Refs for stale-closure-safe category switching
  const lastCategoryChangeRef = useRef<number>(0);
  const touchStartYRef = useRef<number>(0);
  const changeCategoryRef = useRef<(direction: "next" | "prev") => void>(() => {});

  // Slide direction state + left panel ref for scroll-into-view
  const [swipeDirection, setSwipeDirection] = useState<"next" | "prev">("next");
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const categoryItemRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const isNavigate = useMemo(() => {
    return categories.some((category) => category.id === categoryId && category.type === NAVIGATION);
  }, [categories, categoryId]);

  const categoryName = useMemo(() => {
    const category = categories.find((category) => category.id === categoryId);
    return category?.type === NAVIGATION ? category.name : null;
  }, [categories, categoryId]);

  // Visible categories (respects show_menu filter) — used for boundary navigation
  const visibleCategories = useMemo(() => {
    return categories.filter((c) => wantsMobile(c.show_menu));
  }, [categories]);

  // Keep the ref always up-to-date so event listeners never close over stale values
  changeCategoryRef.current = (direction: "next" | "prev") => {
    const now = Date.now();
    if (now - lastCategoryChangeRef.current < 600) return; // throttle rapid changes
    lastCategoryChangeRef.current = now;

    const currentIdx = visibleCategories.findIndex((c) => c.id === categoryId);
    if (direction === "next" && currentIdx < visibleCategories.length - 1) {
      handleSelectCategory(visibleCategories[currentIdx + 1], direction);
    } else if (direction === "prev" && currentIdx > 0) {
      handleSelectCategory(visibleCategories[currentIdx - 1], direction);
    }
  };

  const handleSelectCategoryId = (category: MenuDataType) => {
    !isNavigate && handleNavigate(category.navigate_to);

    setSession(CATEGORY, category.id.toString());
    if (category.type === NAVIGATION && AUTH_ROUTE.includes(category.navigate_to)) {
      onClose && onClose();
    }
  };

  const handleSeeAllClick = (categoryId: number) => {
    const category: MenuDataType | null = categories?.find((category) => category.id === categoryId) || null;
    if (category) {
      isNavigate ? handleNavigate(category.navigate_to) : handleSelectCategoryId(category);
      onClose && onClose();
    }
  };

  const handleSelectCategory = (selectedCategory: MenuDataType, dir?: "next" | "prev") => {
    if (dir) {
      setSwipeDirection(dir);
    } else {
      // Compute direction from index when triggered by a left-panel click
      const currentIdx = visibleCategories.findIndex((c) => c.id === categoryId);
      const newIdx = visibleCategories.findIndex((c) => c.id === selectedCategory.id);
      setSwipeDirection(newIdx >= currentIdx ? "next" : "prev");
    }
    setCategoryId(selectedCategory.id);
    setSession(CATEGORY, selectedCategory.id.toString());

    if (getSession(SELECTED_ITEM)) {
      removeSession(SELECTED_ITEM);
    }
  };

  useEffect(() => {
    if (categoryId !== prevCategoryId) {
      setPrevCategoryId(categoryId);
    }
  }, [categoryId, prevCategoryId]);

  // Scroll the selected category into view in the left panel
  useEffect(() => {
    const el = categoryItemRefs.current[categoryId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [categoryId]);

  // Reset scroll when category changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      // scrollTo with behavior:"smooth" is unreliable on iOS Safari for
      // overflow containers — set scrollTop directly instead.
      scrollContainerRef.current.scrollTop = 0;
      sessionStorage.removeItem(SCROLL_STORAGE_KEY);
    }
  }, [categoryId]);

  useEffect(() => {
    const savedScrollY = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    let timeout: NodeJS.Timeout;

    if (savedScrollY && scrollContainerRef.current) {
      timeout = setTimeout(() => {
        scrollContainerRef.current!.scrollTo({
          top: parseInt(savedScrollY, 10),
          behavior: "smooth",
        });
      }, 400);
    }

    const handleScroll = () => {
      if (scrollContainerRef.current) {
        sessionStorage.setItem(SCROLL_STORAGE_KEY, scrollContainerRef.current.scrollTop.toString());
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
      clearTimeout(timeout);
    };
  }, []);

  // Boundary-scroll and swipe to change category
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const isAtBottom = () => container.scrollTop + container.clientHeight >= container.scrollHeight - 5;
    const isAtTop = () => container.scrollTop <= 5;

    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY > 0 && isAtBottom()) {
        changeCategoryRef.current("next");
      } else if (e.deltaY < 0 && isAtTop()) {
        changeCategoryRef.current("prev");
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      touchStartYRef.current = e.touches[0].clientY;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const deltaY = touchStartYRef.current - e.changedTouches[0].clientY;
      if (Math.abs(deltaY) < 40) return; // ignore tiny taps
      if (deltaY > 0 && isAtBottom()) {
        changeCategoryRef.current("next");
      } else if (deltaY < 0 && isAtTop()) {
        changeCategoryRef.current("prev");
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: true });
    container.addEventListener("touchstart", handleTouchStart, { passive: true });
    container.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener("wheel", handleWheel);
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, []); // empty deps — always reads latest values through changeCategoryRef

  return (
    <div className="flex flex-col bg-menuGradient" style={{ height: "calc(100vh - 70px)", minHeight: "calc(100vh - 70px)" }}>
      <div className="flex flex-1 min-h-0">
        {/* category */}
        <div
          ref={leftPanelRef}
          className={cn("w-1/4 min-w-0 flex flex-col", categories?.length > 5 ? "overflow-y-auto scrollbar-hide" : "overflow-hidden")}
        >
          <div className="flex-1 pb-20">
            {categories.map((category, index) => {
              const isSelected = categoryId === category.id;
              const currentIndex = categories.findIndex((value) => value.id === categoryId);
              const isCurrentCategorySelected = index === currentIndex;
              const isBeforeCategorySelected = index === currentIndex - 1;
              const isAfterCategorySelected = index === currentIndex + 1;
              const isVisible = wantsMobile(category.show_menu);

              return (
                isVisible && (
                  <div
                    key={category?.id}
                    ref={(el) => {
                      categoryItemRefs.current[category.id] = el;
                    }}
                    className={cn(
                      "flex flex-col items-center justify-center pt-3.5 pb-1.5  cursor-pointer border border-transparent relative",
                      isCurrentCategorySelected ? "bg-white text-black rounded-tr-lg" : "bg-secondaryColor text-textColor",
                      isAfterCategorySelected && "rounded-tr-xl",
                      isBeforeCategorySelected && "rounded-br-xl",
                    )}
                  >
                    <motion.div
                      onClick={() => handleSelectCategory(category)}
                      variants={touchVariants}
                      initial="initial"
                      whileTap="tap"
                      className={cn("rounded-md mb-2 relative", isSelected ? "bg-transparent z-40" : "bg-transparent")}
                    >
                      {isCurrentCategorySelected && (
                        <motion.div
                          layoutId="pill-move-tab"
                          transition={{ type: "spring", duration: 0.5 }}
                          className={cn("rounded-md h-[65px] w-[65px] absolute -top-2 -left-2 flex items-center justify-center bg-[#FAEFEA]  z-0")}
                        />
                      )}

                      {/* Foreground Image */}
                      <div className="flex w-[50px] h-[50px] items-center justify-center">
                        <ImageWithSkeleton
                          url={{
                            small: typeof category.img === "string" ? category.img : category.img?.s?.url || FALLBACK_IMAGE_URL,
                            medium: typeof category.img === "string" ? category.img : category.img?.m?.url || FALLBACK_IMAGE_URL,
                            large: typeof category.img === "string" ? category.img : category.img?.l?.url || FALLBACK_IMAGE_URL,
                            hd: typeof category.img === "string" ? category.img : category.img?.hd?.url || FALLBACK_IMAGE_URL,
                            original: typeof category.img === "string" ? category.img : category.img?.og?.url || FALLBACK_IMAGE_URL,
                          }}
                          alt={category.name || "Category Image"}
                          width={50}
                          height={50}
                          className="icon-menu-image "
                          isPreloaded={isImagePreloaded?.(typeof category.img === "string" ? category.img : category.img?.s?.url || "") || false}
                          useSkeletonLoader={true}
                        />
                      </div>
                    </motion.div>

                    <p
                      className={cn(
                        "text-[13px] w-full text-center whitespace-pre-line line-clamp-2",
                        isSelected ? "text-textHeadingColor" : "text-textColorPrimary",
                        medium,
                      )}
                      onClick={() => handleSelectCategory(category)}
                    >
                      {category.name}
                    </p>

                    {isSelected && <div className="absolute -left-0.5 top-[9px] h-[62px] w-1 bg-textHeadingColor rounded-full"></div>}
                  </div>
                )
              );
            })}

            <div
              className={cn(
                "bg-secondaryColor",
                categoryId === categories[categories?.length - 1].id && "rounded-tr-xl ",
                categories?.length > 6 ? "h-[calc(100vh-700px)]" : "h-[calc(100vh-500px)]",
              )}
            />
          </div>
        </div>
        {/* sub category */}
        <div className="flex-1  flex flex-col select-none h-[calc(100dvh-142px)] min-w-0 pt-1 relative">
          {/* if footer display block then this class apply  */}
          <div className="flex-1 overflow-y-auto screen-hight overflow-x-hidden scrollbar-hide pb-24" ref={scrollContainerRef}>
            <AnimatePresence mode="wait" custom={swipeDirection} initial={false}>
              <motion.div
                key={categoryId}
                custom={swipeDirection}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.18, ease: "easeInOut" }}
                className="flex flex-col gap-y-5"
              >
                {categories
                  ?.find((category) => category.id === categoryId)
                  ?.menu_group?.map((_category, index, arr) => {
                    const Component = COMPONENT_MAPPING[_category?.disp_type];
                    const isLast = index === arr.length - 1;
                    const isFirst = index === 0 && _category?.show_name === 1;
                    const isVisible = wantsMobile(_category.show_menu);

                    return (
                      <div key={index} className={cn(isLast ? "pb-10" : "pb-0", isFirst ? "pt-3" : "pt-0")}>
                        {_category.menu_item && isVisible ? (
                          <Component
                            shape={_category?.shape}
                            data={_category.menu_item}
                            displayItem="narrow"
                            category_name={_category?.show_name ? _category.name : null}
                            isImagePreloaded={isImagePreloaded}
                          />
                        ) : null}
                      </div>
                    );
                  })}
              </motion.div>
            </AnimatePresence>
          </div>

          {isNavigate && (
            <div className="bg-[#FFF1E0]  text-textHeadingColor border-t-2 border-t-textHeadingColor h-11 absolute -bottom-1 left-0 right-0 flex items-center py-4 b xs:w-[calc(100dvw-75px)] smd:w-[calc(100dvw-89px)] sml:w-[calc(100dvw-100px)] md:w-[calc(100dvw-470px)] md:-bottom-16">
              <div className="w-full flex items-center justify-center">
                <button
                  onClick={() => handleSeeAllClick(categoryId)}
                  className={cn("flex items-center justify-center px-3 py-2 gap-x-1", medium)}
                  type="button"
                >
                  <span className="text-14">{t("common.see_all", { type: categoryName })}</span>{" "}
                  <span>
                    <IoIosArrowRoundForward color="#d05e2d" size={20} />
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;
